/* eslint-disable no-shadow */
/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Container } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FiFileText,
  FiArrowLeft,
  FiCheckCircle,
  FiActivity,
  FiX,
  FiChevronDown,
  FiChevronUp,
} from 'react-icons/fi';
import { getVisaApplicationByIdApi } from '../../api/onlineVisaApi';
import { formatPrice, formatEntry } from '../../utils/onlineVisaUtils';
import {
  ONLINE_VISA_ROUTES,
  VISA_PAX_TYPES,
} from '../../constants/onlineVisaConstants';
import './OnlineVisaVoucherPage.scss';

const PAX_LABEL = {
  [VISA_PAX_TYPES.ADULT]: 'Adult',
  [VISA_PAX_TYPES.CHILD]: 'Child',
  [VISA_PAX_TYPES.INFANT]: 'Infant',
};

function getInitials(first = '', last = '') {
  const a = (first || '').trim().charAt(0);
  const b = (last || '').trim().charAt(0);
  return `${a}${b}`.toUpperCase() || '—';
}

function titleCase(str) {
  if (!str) return '';
  return str.toLowerCase().replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function formatHistoryDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return dateStr;
  const datePart = d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  const timePart = d.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return `${datePart}, ${timePart}`;
}

const STATUS_LABEL_MAP = {
  ORDER_CREATED: 'Order Created',
  DOCUMENT_UPLOADED: 'Document Uploaded',
  PAYMENT_COMPLETED: 'Payment Completed',
  CONFIRM: 'Payment Completed',
  CONFIRMED: 'Payment Completed',
  PENDING: 'Pending',
  CANCELLED: 'Cancelled',
  REJECTED: 'Rejected',
  APPROVED: 'Approved',
};

function formatStatusLabel(status) {
  if (!status) return 'Status Update';
  const key = status.toUpperCase().replace(/ /g, '_');
  return (
    STATUS_LABEL_MAP[key] ||
    status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

function buildApplicantTimeline(
  applicant,
  applicantHistory,
  applicationStatusHistory,
) {
  const appEvents = (applicationStatusHistory || []).map((h) => ({
    id: `app-${h.id}`,
    label: formatStatusLabel(
      h.status || h.applicant_status || h.booking_status || '',
    ),
    subLabel: h.remarks || '',
    date: h.changed_on || h.created_at || '',
  }));

  const appId = String(applicant.applicant_id || '');
  const paxEvents = (applicantHistory || [])
    .filter((h) => String(h.applicant_id) === appId)
    .map((h) => ({
      id: `hist-${h.history_id}`,
      label: formatStatusLabel(h.applicant_status || ''),
      subLabel: h.remarks || '',
      date: h.changed_on || '',
    }));

  return [...appEvents, ...paxEvents].sort(
    (a, b) =>
      new Date(a.date.replace(' ', 'T')) - new Date(b.date.replace(' ', 'T')),
  );
}

function StatusPill({ status }) {
  const s = (status || '').toUpperCase();
  let label = s || 'PENDING';
  if (s === 'CONFIRM' || s === 'CONFIRMED') label = 'PAYMENT COMPLETED';
  if (s === 'PAYMENT COMPLETED') label = 'PAYMENT COMPLETED';
  return (
    <span
      className={`voucher-page__pill voucher-page__pill--${
        s.toLowerCase() || 'pending'
      }`}
    >
      <span className="voucher-page__pill-dot" />
      {label}
    </span>
  );
}

StatusPill.defaultProps = { status: '' };
StatusPill.propTypes = { status: PropTypes.string };

function OnlineVisaVoucherPage() {
  const navigate = useNavigate();
  const { transactionId } = useParams();
  const txId = decodeURIComponent(transactionId || '');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [statusSidebarOpen, setStatusSidebarOpen] = useState(false);
  const [expandedApplicants, setExpandedApplicants] = useState({});

  useEffect(() => {
    let cancelled = false;
    if (!txId) {
      setLoading(false);
      setError('Missing transaction id in URL.');
      return () => {};
    }
    setLoading(true);
    setError('');
    (async () => {
      const result = await getVisaApplicationByIdApi(txId);
      if (cancelled) return;
      if (!result) {
        setError('Unable to load the booking details. Please try again.');
        setLoading(false);
        return;
      }
      setData(result);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [txId]);

  const application = data?.application || {};
  const applicants = Array.isArray(data?.applicants) ? data.applicants : [];
  const applicantHistory = Array.isArray(data?.applicantHistory)
    ? data.applicantHistory
    : [];
  const applicationStatusHistory = Array.isArray(data?.applicationStatusHistory)
    ? data.applicationStatusHistory
    : [];
  const documents = useMemo(
    () => (Array.isArray(data?.documents) ? data.documents : []),
    [data],
  );
  const visaInvoice = useMemo(() => data?.visaInvoice || {}, [data]);
  const paxFareBreakup = useMemo(() => {
    try {
      if (
        visaInvoice?.farebreakupstring &&
        JSON.parse(visaInvoice.farebreakupstring)?.visaFare?.paxWisePriceList
      ) {
        const paxBreakup = JSON.parse(visaInvoice.farebreakupstring).visaFare
          .paxWisePriceList;
        const map = paxBreakup.reduce((acc, curr) => {
          if (curr.paxType) {
            acc[curr.paxType] = curr;
          }
          return acc;
        }, {});
        return map;
      }
      return {};
    } catch (error) {
      return {};
    }
  }, [visaInvoice]);

  // group documents by applicant_id
  const docsByApplicant = useMemo(() => {
    const map = {};
    documents.forEach((d) => {
      const key = String(d.applicant_id || '');
      if (!map[key]) map[key] = [];
      map[key].push(d);
    });
    return map;
  }, [documents]);

  const destination =
    application?.country_to || application?.destination_name || '';
  const visaTypeLabel = (visaInvoice?.typeofvisa || '').toUpperCase();
  const entryLabel = formatEntry(application?.entry) || '';
  const currency = application?.currency || 'INR';
  const totalAmount = Number(visaInvoice?.totalfarequote) || 0;
  const paymentType = (visaInvoice?.txtype && 'DEPOSIT') || 'DEPOSIT';
  const paymentStatus = application?.booking_status?.toUpperCase() || '';

  const handleBack = () => navigate(-1);
  const handleViewInvoice = () => {
    navigate(
      ONLINE_VISA_ROUTES.INVOICE.replace(
        ':transactionId',
        encodeURIComponent(txId),
      ),
    );
  };
  const handleToggleApplicant = (applicantId) => {
    setExpandedApplicants((prev) => ({
      ...prev,
      [applicantId]: !prev[applicantId],
    }));
  };

  if (loading) {
    return (
      <main className="voucher-page">
        <Container>
          <div className="voucher-page__loader">Loading booking details…</div>
        </Container>
      </main>
    );
  }

  if (error || !data) {
    return (
      <main className="voucher-page">
        <Container>
          <div className="voucher-page__error">
            {error || 'Unable to load the booking details.'}
          </div>
          <button
            type="button"
            className="voucher-page__back-btn"
            onClick={handleBack}
          >
            <FiArrowLeft />
            <span>Back</span>
          </button>
        </Container>
      </main>
    );
  }

  return (
    <main className="voucher-page">
      <Container>
        {/* TOP ACTIONS */}
        <div className="voucher-page__actions">
          <button
            type="button"
            className="voucher-page__back-btn"
            onClick={handleBack}
          >
            <FiArrowLeft />
            <span>Back</span>
          </button>
          <div className="voucher-page__actions-right">
            <button
              type="button"
              className="voucher-page__status-btn"
              onClick={() => setStatusSidebarOpen(true)}
            >
              <FiActivity />
              <span>Application Status</span>
            </button>
            <button
              type="button"
              className="voucher-page__invoice-btn"
              onClick={handleViewInvoice}
            >
              <FiFileText />
              <span>View Invoice</span>
            </button>
          </div>
        </div>

        {/* BLUE HEADER CARD */}
        <section className="voucher-page__header-card">
          <div className="voucher-page__header-top">
            <div>
              <h1 className="voucher-page__destination">
                {titleCase(destination).toUpperCase()}
              </h1>
              <p className="voucher-page__sub">
                {visaTypeLabel || 'VISA'} {entryLabel ? `· ${entryLabel}` : ''}
              </p>
            </div>
            <StatusPill status={paymentStatus} />
          </div>

          <div className="voucher-page__header-grid">
            <div className="voucher-page__info-col">
              <span className="voucher-page__info-label">Application Id</span>
              <strong className="voucher-page__info-value">
                {application?.visa_application_id || txId || '—'}
              </strong>
            </div>
            <div className="voucher-page__info-col">
              <span className="voucher-page__info-label">Visa Status</span>
              <strong className="voucher-page__info-value">
                {application?.visastatus || ''}
              </strong>
            </div>
            <div className="voucher-page__info-col">
              <span className="voucher-page__info-label">Ref No</span>
              <strong className="voucher-page__info-value">
                {application?.application_ref_no || '—'}
              </strong>
            </div>
            <div className="voucher-page__info-col">
              <span className="voucher-page__info-label">Validity</span>
              <strong className="voucher-page__info-value">
                {application?.validity
                  ? `${application.validity} ${
                      application.validity_type || ''
                    }`.trim()
                  : '—'}
              </strong>
            </div>
            <div className="voucher-page__info-col">
              <span className="voucher-page__info-label">Stay</span>
              <strong className="voucher-page__info-value">
                {application?.duration
                  ? `${application.duration} ${
                      application.duration_type || ''
                    }`.trim()
                  : '—'}
              </strong>
            </div>
            <div className="voucher-page__info-col">
              <span className="voucher-page__info-label">Payment</span>
              <strong className="voucher-page__info-value">
                {paymentType}
              </strong>
            </div>
            <div className="voucher-page__info-col">
              <span className="voucher-page__info-label">Total Amount</span>
              <strong className="voucher-page__info-value">
                {`${currency} `}
                {formatPrice(totalAmount, currency) || totalAmount}
              </strong>
            </div>
          </div>
        </section>

        {/* APPLICANTS */}
        <h2 className="voucher-page__section-title">
          APPLICANTS ({applicants.length})
        </h2>

        <div className="voucher-page__applicants">
          {applicants.map((ap, idx) => {
            const paxLabel = PAX_LABEL[ap.pax_type] || ap.pax_type;
            const applicantId = ap.applicant_id;
            const docs = docsByApplicant[String(applicantId)] || [];
            return (
              <div key={applicantId || idx} className="voucher-page__applicant">
                <div className="voucher-page__applicant-head">
                  <div className="voucher-page__avatar" aria-hidden>
                    {getInitials(ap.first_name, ap.last_name)}
                  </div>
                  <div className="voucher-page__applicant-meta">
                    <h3 className="voucher-page__applicant-name">
                      {[ap.title, ap.first_name, ap.last_name]
                        .filter(Boolean)
                        .join(' ')}
                    </h3>
                    <p className="voucher-page__applicant-sub">
                      {paxLabel}
                      {applicantId ? ` · Ref: ${applicantId}` : ''}
                    </p>
                  </div>
                  <StatusPill
                    status={ap.applicant_status || ap.status || paymentStatus}
                  />
                </div>

                <div className="voucher-page__applicant-body">
                  <h4 className="voucher-page__subsection">
                    <FiFileText />
                    <span>Personal Details</span>
                  </h4>
                  <div className="voucher-page__details-grid">
                    <div className="voucher-page__detail">
                      <span className="voucher-page__detail-label">
                        First Name
                      </span>
                      <strong className="voucher-page__detail-value">
                        {ap.first_name || '—'}
                      </strong>
                    </div>
                    <div className="voucher-page__detail">
                      <span className="voucher-page__detail-label">
                        Last Name
                      </span>
                      <strong className="voucher-page__detail-value">
                        {ap.last_name || '—'}
                      </strong>
                    </div>
                    <div className="voucher-page__detail">
                      <span className="voucher-page__detail-label">
                        Nationality
                      </span>
                      <strong className="voucher-page__detail-value">
                        {ap.nationality || visaInvoice?.paxnationality || 'IN'}
                      </strong>
                    </div>
                    <div className="voucher-page__detail">
                      <span className="voucher-page__detail-label">
                        Passport No
                      </span>
                      <strong className="voucher-page__detail-value">
                        {ap.passport_number || '—'}
                      </strong>
                    </div>
                  </div>

                  <h4 className="voucher-page__subsection">
                    <FiFileText />
                    <span>Documents ({docs.length})</span>
                  </h4>
                  <div className="voucher-page__docs-grid">
                    {docs.length === 0 && (
                      <span className="voucher-page__docs-empty">
                        No documents uploaded.
                      </span>
                    )}
                    {docs.map((d) => {
                      const isImage = /\.(jpe?g|png|gif|webp)$/i.test(
                        d.document_url || '',
                      );
                      return (
                        <a
                          key={d.applicant_document_id}
                          href={d.document_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="voucher-page__doc-card"
                        >
                          <div className="voucher-page__doc-thumb">
                            {isImage ? (
                              <img
                                src={d.document_url}
                                alt={d.document_name}
                                loading="lazy"
                              />
                            ) : (
                              <FiFileText />
                            )}
                          </div>
                          <div className="voucher-page__doc-meta">
                            <span className="voucher-page__doc-name">
                              {d.document_name}
                            </span>
                            <span className="voucher-page__doc-badge">
                              <FiCheckCircle />
                              Doc Uploaded
                            </span>
                          </div>
                        </a>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* PRICING SUMMARY */}
        <h2 className="voucher-page__section-title">Pricing Summary</h2>

        <div className="voucher-page__pricing">
          {Object.keys(paxFareBreakup).length > 0 &&
            Object.keys(paxFareBreakup).map((key) => {
              const pw = paxFareBreakup[key];
              const paxType = PAX_LABEL[pw.paxType] || pw.paxType;
              const count = pw.paxCount || 1;
              const baseFare = Number(pw.paxTotalAmount || 0);
              const tax = Number(pw.PaxHandleCharges + pw.PaxTfeeInv || 0);
              const handlingCharges = Number(
                pw.PaxHiddenMarkupOnBaseFare + pw.hiddenMarkup || 0,
              );
              // const comission = Number(pw.paxCommission || 0);
              // const tds = Number(pw.paxTds || 0);
              // const totalNet = Number(pw.PaxInvoiceTotal || 0);
              const totalFareQuote = Number(pw.PaxFareQuoteTotal || 0);
              // const perPax = Number(
              //   pw.PaxFareQuoteTotal / pw.paxCount || 0,
              // );
              return (
                <div key={key} className="voucher-page__pricing-row">
                  <div className="voucher-page__pricing-pax">
                    {paxType} × {count}
                  </div>
                  <div className="voucher-page__pricing-chips">
                    <div className="voucher-page__pricing-chip">
                      <span className="voucher-page__pricing-chip-label">
                        Base Fare
                      </span>
                      <span className="voucher-page__pricing-chip-value">
                        {formatPrice(baseFare, currency) ||
                          `${currency} ${baseFare}`}
                      </span>
                    </div>
                    <div className="voucher-page__pricing-chip">
                      <span className="voucher-page__pricing-chip-label">
                        Tax
                      </span>
                      <span className="voucher-page__pricing-chip-value">
                        {formatPrice(tax, currency) || `${currency} ${tax}`}
                      </span>
                    </div>
                    <div className="voucher-page__pricing-chip">
                      <span className="voucher-page__pricing-chip-label">
                        Handling Charges
                      </span>
                      <span className="voucher-page__pricing-chip-value">
                        {formatPrice(handlingCharges, currency) ||
                          `${currency} ${handlingCharges}`}
                      </span>
                    </div>
                    <div className="voucher-page__pricing-chip voucher-page__pricing-chip--total">
                      <span className="voucher-page__pricing-chip-label">
                        Subtotal
                      </span>
                      <span className="voucher-page__pricing-chip-value">
                        {formatPrice(totalFareQuote, currency) ||
                          `${currency} ${totalFareQuote}`}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}

          <div className="voucher-page__pricing-total">
            <span>Grand Total</span>
            <strong>
              {formatPrice(totalAmount, currency) ||
                `${currency} ${totalAmount}`}
            </strong>
          </div>
        </div>
      </Container>

      {/* APPLICATION STATUS SIDEBAR */}
      {statusSidebarOpen && (
        <div
          className="voucher-page__sidebar-overlay"
          role="presentation"
          onClick={() => setStatusSidebarOpen(false)}
        />
      )}
      <aside
        className={`voucher-page__sidebar${
          statusSidebarOpen ? ' voucher-page__sidebar--open' : ''
        }`}
        aria-label="Application Status"
      >
        <div className="voucher-page__sidebar-header">
          <h2 className="voucher-page__sidebar-title">Application Status</h2>
          <button
            type="button"
            className="voucher-page__sidebar-close"
            onClick={() => setStatusSidebarOpen(false)}
            aria-label="Close"
          >
            <FiX />
          </button>
        </div>

        <div className="voucher-page__sidebar-body">
          {applicants.map((ap) => {
            const applicantId = ap.applicant_id;
            const paxLabel = PAX_LABEL[ap.pax_type] || ap.pax_type || '';
            const isExpanded = !!expandedApplicants[applicantId];
            const timeline = buildApplicantTimeline(
              ap,
              applicantHistory,
              applicationStatusHistory,
            );
            const currentStatus = formatStatusLabel(
              ap.applicant_status || ap.status || paymentStatus || '',
            );

            return (
              <div key={applicantId} className="voucher-page__sidebar-card">
                <button
                  type="button"
                  className="voucher-page__sidebar-card-head"
                  onClick={() => handleToggleApplicant(applicantId)}
                >
                  <div className="voucher-page__avatar voucher-page__avatar--sm">
                    {getInitials(ap.first_name, ap.last_name)}
                  </div>
                  <div className="voucher-page__applicant-meta">
                    <span className="voucher-page__applicant-name">
                      {[ap.title, ap.first_name, ap.last_name]
                        .filter(Boolean)
                        .join(' ')}
                    </span>
                    <span className="voucher-page__applicant-sub">
                      {paxLabel}
                      {timeline.length ? ` · ${timeline.length} events` : ''}
                    </span>
                  </div>
                  <span className="voucher-page__sidebar-status">
                    {currentStatus}
                  </span>
                  {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                </button>

                {isExpanded && (
                  <div className="voucher-page__timeline">
                    {timeline.length === 0 && (
                      <p className="voucher-page__timeline-empty">
                        No history available.
                      </p>
                    )}
                    {timeline.map((ev, idx) => {
                      const isCurrent = idx === timeline.length - 1;
                      return (
                        <div
                          key={ev.id}
                          className="voucher-page__timeline-item"
                        >
                          <div
                            className={`voucher-page__timeline-dot${
                              isCurrent
                                ? ' voucher-page__timeline-dot--active'
                                : ''
                            }`}
                          />
                          <div className="voucher-page__timeline-content">
                            <span className="voucher-page__timeline-label">
                              {ev.label}
                            </span>
                            {ev.subLabel && (
                              <span className="voucher-page__timeline-sub">
                                {ev.subLabel}
                              </span>
                            )}
                            <span className="voucher-page__timeline-date">
                              {formatHistoryDate(ev.date)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </aside>
    </main>
  );
}

export default OnlineVisaVoucherPage;
